import { useCallback, useMemo } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import {
  useNavigate,
  useSearchParams,
  useParams,
} from "react-router-dom-v5-compat";
import { PACKAGES_QUERY_PARAM, PackageType } from "@b2bportal/core-packages";
import {
  ensureExhaustive,
  getPackageRoutes,
  HOTEL_URL_PARAM_KEYS,
} from "@b2bportal/core-utilities";
import { HotelsShopSelectors } from "@tenant/hotels";
import { useIsUpSell } from "@/overrides/packages/hooks";
import {
  type AppDispatch,
  type RootState,
  PackageHotelShopSelectors,
  PackageBaseSelectors,
} from "@/store";
import { PackagesBaseActions } from "@/store/packages/slice/packages";

export const usePackageHotelShopNavigation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const store = useStore<RootState>();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const newParams = useMemo(
    () => new URLSearchParams(queryParams),
    [queryParams]
  );

  const { lodgingName, lodgingId } = useParams<{
    lodgingName?: string;
    lodgingId?: string;
  }>();

  const packagesByRateId = useSelector(
    PackageHotelShopSelectors.getPackagesByRateId
  );

  const packageType = useSelector(
    PackageBaseSelectors.getCurrentActivePackageType
  );

  const isUpSell = useIsUpSell();

  const goToNextStepRoute = useCallback(() => {
    switch (packageType) {
      case PackageType.HOTEL_FLIGHT_CAR:
      case PackageType.HOTEL_FLIGHT:
        navigate(
          isUpSell
            ? `${getPackageRoutes().FULL_ROUTES.CHECKOUT}?${newParams.toString()}`
            : `${getPackageRoutes().FULL_ROUTES.FLIGHTS.DEPARTURE}?${newParams.toString()}`
        );
        break;
      case PackageType.HOTEL_CAR:
        navigate(
          `${getPackageRoutes().FULL_ROUTES.CARS.AVAILABILITY}?${newParams.toString()}`
        );
        break;
      case PackageType.FLIGHT_CAR:
        // Shouldn't ever hit here if in hotels, kick back to search/homepage if so.
        navigate(getPackageRoutes().BASE_ROUTE);
        break;
      default:
        ensureExhaustive(packageType);
    }
  }, [navigate, packageType, newParams, isUpSell]);

  const goToRoomSelectionPage = useCallback(() => {
    if (!lodgingName || !lodgingId) {
      console.error("Lodging name or ID is missing");
      return;
    }
    const routes = getPackageRoutes();
    const path = routes.FULL_ROUTES.HOTELS.ROOM_SELECTION.replace(
      ":lodgingName?",
      lodgingName
    ).replace(":lodgingId", lodgingId);
    navigate(`${path}?${queryParams.toString()}`);
  }, [queryParams, navigate, lodgingName, lodgingId]);

  const onRoomSelection = useCallback(() => {
    // Read at call time so we see the selection just dispatched by RoomSelectionPage
    // (closure over useSelector would be stale and cause re-navigation loop)
    const currentSelectedRoomRateId = HotelsShopSelectors.getSelectedRoomRateId(
      store.getState()
    );
    if (currentSelectedRoomRateId) {
      const packageToken = packagesByRateId[currentSelectedRoomRateId];
      if (packageToken) {
        dispatch(PackagesBaseActions.setCurrentActivePackage(packageToken));
        newParams.delete(HOTEL_URL_PARAM_KEYS.LAT_LNG);
        newParams.delete(HOTEL_URL_PARAM_KEYS.ZOOM);
        newParams.set(
          PACKAGES_QUERY_PARAM.packageToken,
          packageToken.token.value
        );
        // need to keep lodgingId in the url for checkout
        newParams.set(HOTEL_URL_PARAM_KEYS.LODGING_SELECTION, lodgingId);
        goToNextStepRoute();
      } else {
        // If we have a selected room but no package token for it (e.g. after navigating back),
        // go to room selection page to let user select again
        goToRoomSelectionPage();
      }
    } else {
      // No selection yet (e.g. user navigated here from elsewhere); go to room selection
      goToRoomSelectionPage();
    }
  }, [
    dispatch,
    goToNextStepRoute,
    goToRoomSelectionPage,
    lodgingId,
    newParams,
    packagesByRateId,
    store,
  ]);

  return {
    handlers: {
      goToNextStepRoute,
      onRoomSelection,
    },
  };
};
